import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import VetRecordUpload from './VetRecordUpload';
import ServiceRecordForm from './ServiceRecordForm';
import { fmtDate } from '../../lib/dates';

// Vet records on the horse detail page. Fetches its own list so the horse
// detail route stays untouched. Add/delete gated to owner + manager.
// The PDF link points at the authed Worker download endpoint (the bucket is
// private — opening it sends the .stallside.io session cookie).

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const TYPE_LABELS = {
  coggins: 'Coggins',
  vaccination: 'Vaccination',
  farrier: 'Farrier',
  dental: 'Dental',
  vet: 'Vet',
  injection: 'Injection',
  injury: 'Injury',
  deworming: 'Deworming',
  other: 'Other',
};

export default function VetRecordsCard({ horseId, horse, owners = [] }) {
  const { activeRole } = useAuth();
  const canEdit = activeRole === 'owner' || activeRole === 'manager';
  const qc = useQueryClient();
  // null | 'service' (typed by hand) | 'upload' (extracted from a PDF)
  const [adding, setAdding] = useState(null);

  const recordsQ = useQuery({
    queryKey: ['horse-records', horseId],
    queryFn: () => api.get(`/horses/${horseId}/records`),
  });

  const remove = useMutation({
    mutationFn: (recordId) => api.del(`/horses/${horseId}/records/${recordId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['horse-records', horseId] }),
  });

  const records = recordsQ.data?.records ?? [];

  return (
    <section className="card p-4 mt-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="font-medium">Vet records</h2>
        {canEdit && !adding && (
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => setAdding('service')}
              className="text-sm text-ink-600 hover:text-ink-900"
            >
              + Add Service
            </button>
            <button
              onClick={() => setAdding('upload')}
              className="text-sm text-ink-600 hover:text-ink-900"
            >
              + Attach PDF
            </button>
          </div>
        )}
      </div>

      {adding === 'service' && (
        <ServiceRecordForm
          horseId={horseId}
          onDone={() => {
            setAdding(null);
            qc.invalidateQueries({ queryKey: ['horse-records', horseId] });
            qc.invalidateQueries({ queryKey: ['dashboard'] });
          }}
          onCancel={() => setAdding(null)}
        />
      )}

      {adding === 'upload' && (
        <VetRecordUpload
          horseId={horseId}
          horse={horse}
          owners={owners}
          onDone={() => {
            setAdding(null);
            qc.invalidateQueries({ queryKey: ['horse-records', horseId] });
            qc.invalidateQueries({ queryKey: ['horse-providers', horseId] });
            qc.invalidateQueries({ queryKey: ['providers'] });
            qc.invalidateQueries({ queryKey: ['dashboard'] });
          }}
          onCancel={() => setAdding(null)}
        />
      )}

      {recordsQ.isLoading ? (
        <p className="text-sm text-ink-400">Loading…</p>
      ) : records.length === 0 && !adding ? (
        <p className="text-sm text-ink-400">
          Nothing recorded yet. “Add service” logs a visit; “Attach PDF” files a document.
        </p>
      ) : (
        <ul className="divide-y divide-ink-100">
          {records.map((r) => (
            <li key={r.id} className="py-2 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-ink-100 text-ink-600">
                    {TYPE_LABELS[r.recordType] || r.recordType}
                  </span>
                  <span className="font-medium text-ink-900 truncate">
                    {r.title || r.providerName || 'Record'}
                  </span>
                </div>
                <div className="text-sm text-ink-500 truncate">
                  {fmtDate(r.date, 'medium')}
                  {r.providerName ? ` · ${r.providerName}` : ''}
                  {r.dueDate ? ` · next due ${fmtDate(r.dueDate, 'medium')}` : ''}
                  {r.dueDate && r.remindDaysBefore != null
                    ? ` · reminder ${r.remindDaysBefore}d before`
                    : ''}
                </div>
                {r.details && (
                  <p className="text-sm text-ink-500 mt-0.5 whitespace-pre-wrap">{r.details}</p>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {r.hasFile && r.fileUrl && (
                  <a
                    href={`${API_BASE}${r.fileUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-ink-600 hover:text-ink-900 underline"
                  >
                    PDF
                  </a>
                )}
                {canEdit && (
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          'Delete this record? It goes to the archive with its PDF, and can be restored.',
                        )
                      )
                        remove.mutate(r.id);
                    }}
                    disabled={remove.isPending}
                    className="text-ink-300 hover:text-red-600"
                    aria-label="Delete record"
                  >
                    ✕
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
